// Data contracts between the pipeline (tools/), the static airport data (src/data) and the simulation.
import type { LatLon } from '../sim/geo';

// ---------------------------------------------------------------------------------------------
// Generated aerodrome geometry (tools/build-airports.ts → src/data/generated/<icao>.geo.json)
// ---------------------------------------------------------------------------------------------
export type EdgeKind = 'rwy' | 'twy' | 'lane' | 'stand';
export const EDGE_KINDS: readonly EdgeKind[] = ['rwy', 'twy', 'lane', 'stand'];
export type Pt = [number, number];

export interface GeoHold { node: number; ref: string; type: 'runway' | 'ILS' | 'intermediate' }
export interface GeoStand {
  ref: string;
  /** Stop position of the nose wheel. */
  node: number;
  /** Graph nodes from the stop position to the first node of the taxi network (inclusive). */
  lead: number[];
  /** Heading of the parked aircraft (true, degrees). */
  hdg: number;
  osmId: number;
}
export interface GeoRunway {
  osmRef: string;
  width: number;
  /** Centre-line graph nodes from one end to the other. */
  nodes: number[];
  lengthTag?: number;
}
export interface GeoBuilding { pts: Pt[]; h: number; kind: 'terminal' | 'hangar' | 'tower' | 'building' | 'parking' }
export interface GeoLine { pts: Pt[]; width: number; name: string; kind: EdgeKind | 'displaced' | 'stopway' | 'closed' }

export interface AirportGeo {
  icao: string;
  attribution: string;
  osmBase: string;
  origin: LatLon;
  nodes: Pt[];
  /** [a, b, kind index into EDGE_KINDS, name index into names] */
  edges: [number, number, number, number][];
  names: string[];
  holds: GeoHold[];
  stands: GeoStand[];
  runways: GeoRunway[];
  draw: {
    aprons: Pt[][];
    lines: GeoLine[];
    buildings: GeoBuilding[];
    helipads: Pt[][];
    boundary: Pt[][];
  };
  towers: Pt[];
  warnings: string[];
}

// ---------------------------------------------------------------------------------------------
// Hand-maintained airport meta data (AIP and other public sources) – src/data/airports/<icao>.ts
// ---------------------------------------------------------------------------------------------
/** Functions (learning stages) a controller can perform. */
export type Role = 'DEL' | 'APN' | 'GND' | 'TWR' | 'DEP' | 'APP' | 'DIR' | 'CTR';
export const ROLES: readonly Role[] = ['DEL', 'APN', 'GND', 'TWR', 'DEP', 'APP', 'DIR', 'CTR'];

export interface UnitMeta {
  /** e.g. 'GND' – unique within the airport. */
  id: string;
  callsign: string;
  /** 8.33 kHz channel name, e.g. '121.755'. */
  freq: string;
  /** Functions this unit performs in reality. */
  roles: Role[];
  /** false = training position that does not exist in this form (documented in DATA_SOURCES.md). */
  real: boolean;
  approx?: boolean;
  /** Radar units: area of responsibility. */
  sector?: SectorMeta;
}
export interface SectorMeta {
  /** Lateral limit around the ARP in NM (simplified circular sector). */
  radiusNm: number;
  floorFt: number;
  ceilingFt: number;
}

export interface FixMeta {
  lat: number;
  lon: number;
  type: 'vor' | 'ndb' | 'dme' | 'rnav' | 'iaf' | 'if' | 'faf' | 'entry' | 'exit' | 'gate' | 'vrp';
  /** How the fix is pronounced on frequency (defaults to the five-letter code spelled as a word). */
  say?: string;
  approx?: boolean;
  note?: string;
}

/** Altitude/speed constraint at a procedure fix. */
export interface ProcLeg {
  fix: string;
  /** 'at', 'above' (at or above) or 'below' (at or below), in feet AMSL. */
  alt?: { kind: 'at' | 'above' | 'below'; ft: number };
  speedKt?: number;
  flyOver?: boolean;
}
export interface SidMeta {
  name: string;
  /** Runway end, e.g. '26'. */
  rwy: string;
  /** Exit point that connects to the route network. */
  exit: string;
  /** Initial climb clearance in feet (part of the IFR clearance). */
  initialAltFt: number;
  /** Track to fly after take-off before the first fix (true degrees), if the SID starts with a heading leg. */
  initialHdg?: number;
  /** Altitude at which the initial heading leg ends. */
  turnAltFt?: number;
  legs: ProcLeg[];
  approx?: boolean;
}
export interface StarMeta {
  name: string;
  entry: string;
  /** Configurations (runway ends) the arrival is used for. */
  rwys: string[];
  legs: ProcLeg[];
  /** Holding fix at the end of the arrival. */
  iaf: string;
  approx?: boolean;
}
export interface HoldingMeta { fix: string; inboundCourse: number; turn: 'L' | 'R'; minFt: number; maxFt?: number; approx?: boolean }
export interface IlsMeta {
  rwy: string;
  /** Localizer course (true degrees, usually runway true heading). */
  course: number;
  /** Glide path angle in degrees. */
  gp: number;
  /** Final approach fix / glide path intercept altitude in ft. */
  faf: { distNm: number; altFt: number };
  freq?: string;
  ident?: string;
}

export interface RunwayEndMeta {
  /** Threshold from the AIP (optional – otherwise the OSM centre-line end is used). */
  thr?: LatLon;
  /** Landing threshold displacement in metres from the runway end. */
  displacedM?: number;
  elevFt?: number;
  /** Take-off run available from the full length and per intersection (holding point name → metres). */
  tora?: Record<string, number>;
  /** Exit taxiways usable after landing in this direction, in order of distance. */
  exits?: string[];
  /** Intersection used by default for departures by heavy jets etc. */
  ils?: IlsMeta;
}
export interface RunwayMeta {
  /** Real designator pair, e.g. '08/26'. */
  id: string;
  /** Matches the OSM runway ref (OSM may still carry an outdated designator). */
  osmRef: string;
  ends: Record<string, RunwayEndMeta>;
  /** Runway is not used by the simulation (e.g. closed). */
  closed?: boolean;
}

export interface RunwayConfigMeta {
  id: string;
  name: string;
  /** Runway ends used for departures and arrivals. */
  dep: string[];
  arr: string[];
  /** Typical wind direction for this configuration and its statistical share. */
  windDir: number;
  share: number;
}

export interface TwyLimit { maxSpan?: number; blockedFromSpan?: number; restricted?: string }
export interface StandMeta {
  /** OSM ref → AIP stand number. */
  map?: Record<string, string>;
  /** OSM stands that do not exist in the AIP. */
  drop?: string[];
  info?: Record<string, { maxSpan?: number; push?: boolean; cargo?: boolean; ga?: boolean; say?: string }>;
  /** Default maximum wing span for stands without info. */
  defaultMaxSpan?: number;
}

export interface AirlineMeta {
  icao: string;
  /** Radiotelephony designator (ICAO Doc 8585). */
  telephony: string;
  fleet: string[];
  dest: string[];
  weight: number;
  /** Flight number style. */
  num: 'num' | 'alnum' | 'reg' | 'short';
  cargo?: boolean;
}
export interface DestMeta { icao: string; name: string; sayAs?: string; lat: number; lon: number }

export interface TrafficMeta {
  airlines: AirlineMeta[];
  destinations: DestMeta[];
  /** Typical movements per hour at peak (both directions). */
  peakMovementsPerHour: number;
  /** Share of arrivals at peak. */
  arrivalShare: number;
  /** VFR / general aviation share and helicopter movements. */
  vfrShare: number;
}

export interface SourceNote { topic: string; source: string; approx?: boolean }

export interface AirportMeta {
  icao: string;
  iata: string;
  name: string;
  /** Name used on frequency (e.g. 'Munich' for München). */
  say: string;
  city: string;
  arp: LatLon;
  elevFt: number;
  /** Magnetic variation, degrees east positive (WMM, epoch of the data). */
  magVar: number;
  transitionAltFt: number;
  airac: string;
  tower: { lat: number; lon: number; eyeHeightM: number; approx?: boolean };
  runways: RunwayMeta[];
  configs: RunwayConfigMeta[];
  units: UnitMeta[];
  /** Which unit performs each learning stage at this airport (stage → unit id). */
  stages: Partial<Record<Role, string>>;
  fixes: Record<string, FixMeta>;
  sids: SidMeta[];
  stars: StarMeta[];
  holdings: HoldingMeta[];
  /** Minimum vectoring altitudes (simplified rings around the ARP). */
  mva: { radiusNm: number; ft: number }[];
  /** Control zone boundary (lat/lon polygon) and other airspace drawn on the radar map. */
  airspace: { name: string; kind: 'CTR' | 'TMA' | 'RMZ' | 'ED-R'; lowerFt: number; upperFt: number; poly: [number, number][]; approx?: boolean }[];
  stands: StandMeta;
  twyLimits?: Record<string, TwyLimit>;
  /** Hand-off points between apron/ground (node references resolved at runtime by nearest holding point name). */
  transitionAltSay?: string;
  traffic: TrafficMeta;
  /** Squawk code block allocated to this airport (simplified ORCAM range). */
  squawkRange: [number, number];
  sources: SourceNote[];
  /** Short description for the career screen. */
  blurb: string;
}
